import { KeyValue } from "../../../interfaces";
import { summaryTablesReportTypeOptions } from "../../../refdata/constants";
import { selectedStatesAlpha } from "../../../refdata/FilterOptions";

interface InsuredObject {
  INSURED: string;
  count: number;
  unique_count: number;
  sum_DEPSUMBR: number;
}

export const addDataIfNull = (data: InsuredObject[]): InsuredObject[] => {
  const dataArray = [];
  for (const dataElem of data) {
    dataArray.push(dataElem.INSURED);
  }
  if (!dataArray.includes("CB")) {
    data.push({ INSURED: "CB", count: 0, unique_count: 0, sum_DEPSUMBR: 0 });
  }
  if (!dataArray.includes("SA")) {
    data.push({ INSURED: "SA", count: 0, unique_count: 0, sum_DEPSUMBR: 0 });
  }
  if (!dataArray.includes("IB")) {
    data.push({ INSURED: "IB", count: 0, unique_count: 0, sum_DEPSUMBR: 0 });
  }
  return data;
};

export function transformData(
  inputData: {
    INSURED: string;
    count: number;
    unique_count: number;
    sum_DEPSUMBR: number;
  }[],
): {
  [key: string]: {
    count: number;
    unique_count: number;
    sum_DEPSUMBR: number;
  };
} {
  const transformedData: {
    [key: string]: {
      count: number;
      unique_count: number;
      sum_DEPSUMBR: number;
    };
  } = {};

  for (const item of inputData) {
    transformedData[item.INSURED] = {
      count: item.count,
      unique_count: item.unique_count,
      sum_DEPSUMBR: item.sum_DEPSUMBR,
    };
  }

  return transformedData;
}

export const summaryTableHeader = (reportType: string) =>
  summaryTablesReportTypeOptions.find(
    (options: KeyValue) => reportType === options.value,
  )?.header;

export const assetSizeLookup: KeyValue = {
  upTo25Million: "Less than $25 Million",
  upTo50Million: "$25 Million to $50 Million",
  upTo100Million: "$50 Million to $100 Million",
  upTo300Million: "$100 Million to $300 Million",
  upTo500Million: "$300 Million to $500 Million",
  upTo1Billion: "$500 Million to $1 Billion",
  upTo10Billion: "$1 Billion to $10 Billion",
  upTo100Billion: "$10 Billion to $100 Billion",
  upTo250Billion: "$100 Billion to $250 Billion",
  over250Billion: "Greater than $250 Billion",
  nationalTotals: "National Totals",
};

export const transformStateName = (value: string) => {
  const selectedState = selectedStatesAlpha.find(
    (state) => state.value === value,
  );
  return selectedState ? selectedState?.label : "";
};

//transforms the data for a Summary table to be downloaded as XLS - Formatted
export const transformDataForDownload = (
  tableData: KeyValue,
  lastYear: string,
  dataType: string,
  stname?: string,
) => {
  const response = [];
  if (stname) {
    stname = transformStateName(stname);
  }
  const assetSize: KeyValue = {
    upTo25Million: "Less than $25 Million",
    upTo50Million: "$25 Million to $50 Million",
    upTo100Million: "$50 Million to $100 Million",
    upTo300Million: "$100 Million to $300 Million",
    upTo500Million: "$300 Million to $500 Million",
    upTo1Billion: "$500 Million to $1 Billion",
    upTo10Billion: "$1 Billion to $10 Billion",
    upTo100Billion: "$10 Billion to $100 Billion",
    upTo250Billion: "$100 Billion to $250 Billion",
    over250Billion: "Greater than $250 Billion",
    nationalTotals: "National Totals",
  };
  let assetKey = false;
  if (dataType === "Asset Size") {
    assetKey = true;
  }
  if (dataType === "State") {
    assetKey = false;
  }
  if (dataType === "stateCharterClass" && stname) {
    for (const key in tableData) {
      const data = {
        "Report Period": lastYear,
        "State": stname,
        "State Charter Class": key,
        "All Institutions": tableData[key]["all"].unique_count,
        "All Offices": tableData[key]["all"].count?.toLocaleString(),
        "All Deposits (Millions USD)": tableData[key]["all"].sum_DEPSUMBR
          ? (tableData[key]["all"].sum_DEPSUMBR / 1000)?.toLocaleString(
              undefined,
              {
                maximumFractionDigits: 0,
              },
            )
          : 0,
        "Headquartered In State Institutions":
          tableData[key]["hqInState"].unique_count,
        "Headquartered In State Offices":
          tableData[key]["hqInState"].count?.toLocaleString(),
        "Headquartered In State Deposits (Millions USD)": tableData[key][
          "hqInState"
        ].sum_DEPSUMBR
          ? (tableData[key]["hqInState"].sum_DEPSUMBR / 1000)?.toLocaleString(
              undefined,
              {
                maximumFractionDigits: 0,
              },
            )
          : 0,
        "Headquartered Out of State Institutions":
          tableData[key]["hqOutOfState"].unique_count,
        "Headquartered Out of State Offices":
          tableData[key]["hqOutOfState"].count?.toLocaleString(),
        "Headquartered Out of State Deposits (Millions USD)": tableData[key][
          "hqOutOfState"
        ]
          ? (
              tableData[key]["hqOutOfState"].sum_DEPSUMBR / 1000
            )?.toLocaleString(undefined, {
              maximumFractionDigits: 0,
            })
          : 0,
      };
      response.push(data);
    }
  } else {
    const institutionCount = (key: string, type: string) => {
      const count =
        tableData[key][type]?.sum_BKMO ||
        tableData[key][type]?.unique_count ||
        0;
      return count?.toLocaleString();
    };
    for (const key in tableData) {
      if (dataType === "County" && stname) {
        const data = {
          "Report Period": lastYear,
          "State": stname,
          [dataType]: key,
          "All Institutions": institutionCount(key, "all"),
          "All Offices": tableData[key].all.count?.toLocaleString(),
          "All Deposits (Millions USD)": (
            tableData[key].all.sum_DEPSUMBR / 1000
          )?.toLocaleString(undefined, {
            maximumFractionDigits: 0,
          }),
          "Commercial Bank Institutions": institutionCount(key, "CB"),
          "Commercial Bank Offices": tableData[key].CB?.count?.toLocaleString(),
          "Commercial Bank Total Deposits (Millions USD)": (
            tableData[key].CB?.sum_DEPSUMBR / 1000
          )?.toLocaleString(undefined, {
            maximumFractionDigits: 0,
          }),
          "Savings Institutions": institutionCount(key, "SA"),
          "Savings Offices": tableData[key].SA?.count?.toLocaleString(),
          "Savings Institutions Total Deposits (Millions USD)": (
            tableData[key].SA?.sum_DEPSUMBR / 1000
          )?.toLocaleString(undefined, {
            maximumFractionDigits: 0,
          }),
          "U.S. Foreign Institutions": institutionCount(key, "IB"),
          "U.S. Foreign Offices": tableData[key].IB?.count?.toLocaleString(),
          "U.S. Foreign Total Deposits (Millions USD)": (
            tableData[key].IB?.sum_DEPSUMBR / 1000
          )?.toLocaleString(undefined, {
            maximumFractionDigits: 0,
          }),
        };
        response.push(data);
      } else {
        const data = {
          "Report Period": lastYear,
          [dataType]: assetKey ? assetSize[key] : key,
          "All Institutions": institutionCount(key, "all"),
          "All Offices": tableData[key].all?.count?.toLocaleString(),
          "All Deposits (Millions USD)": (
            tableData[key].all?.sum_DEPSUMBR / 1000
          )?.toLocaleString(undefined, {
            maximumFractionDigits: 0,
          }),
          "Commercial Bank Institutions": institutionCount(key, "CB"),
          "Commercial Bank Offices": tableData[key].CB?.count?.toLocaleString(),
          "Commercial Bank Total Deposits (Millions USD)": (
            tableData[key].CB?.sum_DEPSUMBR / 1000
          )?.toLocaleString(undefined, {
            maximumFractionDigits: 0,
          }),
          "Savings Institutions": institutionCount(key, "SA"),
          "Savings Offices": tableData[key]?.SA?.count?.toLocaleString(),
          "Savings Institutions Total Deposits (Millions USD)": (
            tableData[key].SA?.sum_DEPSUMBR / 1000
          )?.toLocaleString(undefined, {
            maximumFractionDigits: 0,
          }),
          "U.S. Foreign Institutions": institutionCount(key, "IB"),
          "U.S. Foreign Offices": tableData[key].IB?.count?.toLocaleString(),
          "U.S. Foreign Total Deposits (Millions USD)": (
            tableData[key].IB?.sum_DEPSUMBR / 1000
          )?.toLocaleString(undefined, {
            maximumFractionDigits: 0,
          }),
        };
        response.push(data);
      }
    }
  }
  return response;
};
